"use client";

import { Check, Minus } from "lucide-react";
import { useTranslations } from "next-intl";
import { DelayAsterisk } from "../delay-asterisk";
import { SectionEyebrow, SectionTitle } from "../section-eyebrow";
import { ScrollReveal, ScrollStagger, StaggerItem } from "../scroll-reveal";

type Row = {
  label: string;
  agency: string;
  freelance: string;
  kessem: string;
  win: boolean;
};

export function Compare() {
  const t = useTranslations("compare");
  const headers = t.raw("headers") as {
    agency: string;
    freelance: string;
    kessem: string;
  };
  const rows = t.raw("rows") as Row[];

  return (
    <section className="relative py-12 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ScrollReveal as="header" className="mb-10 flex flex-col gap-5 sm:max-w-2xl lg:mb-14">
          <div className="flex items-center justify-between">
            <SectionEyebrow>{t("eyebrow")}</SectionEyebrow>
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 sm:inline">
              {t("annotation")}
            </span>
          </div>
          <SectionTitle text={t("title")} italicLastWords={2} />
          <p data-blend className="text-base sm:text-lg">
            {t("subtitle")}
          </p>
        </ScrollReveal>

        {/* Mobile — stacked cards (jusqu'à md) */}
        <ScrollStagger as="ul" className="flex flex-col gap-3 md:hidden" staggerChildren={0.06}>
          {rows.map((row) => (
            <StaggerItem
              key={row.label}
              as="li"
              className="rounded-2xl border border-border bg-card p-4"
            >
              <p className="mb-3 font-sans text-sm font-medium text-foreground">{row.label}</p>
              <div className="grid grid-cols-3 divide-x divide-border/60 rtl:divide-x-reverse">
                <MobileCell header={headers.agency} value={row.agency} />
                <MobileCell header={headers.freelance} value={row.freelance} />
                <MobileCell header={headers.kessem} value={row.kessem} highlighted showAsterisk={row === rows[0]} />
              </div>
            </StaggerItem>
          ))}
        </ScrollStagger>

        {/* Tablet & desktop — comparison table (md+) */}
        <ScrollReveal className="hidden md:block">
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <div className="grid grid-cols-[1.6fr_repeat(3,1fr)] items-stretch border-b border-border bg-muted/40">
              <div className="px-5 py-4" />
              <ColumnHead label={headers.agency} muted />
              <ColumnHead label={headers.freelance} muted />
              <ColumnHead label={headers.kessem} highlighted />
            </div>

            <ScrollStagger as="ul" staggerChildren={0.04} amount={0.05}>
              {rows.map((row, i) => (
                <StaggerItem
                  key={row.label}
                  as="li"
                  className={`grid grid-cols-[1.6fr_repeat(3,1fr)] items-center transition-colors last:border-b-0 hover:bg-muted/30 ${
                    i % 2 === 0 ? "bg-background" : "bg-card"
                  }`}
                >
                  <div className="px-5 py-5 text-base font-medium text-foreground">{row.label}</div>
                  <Cell value={row.agency} muted />
                  <Cell value={row.freelance} muted />
                  <Cell value={row.kessem} highlighted showAsterisk={i === 0} />
                </StaggerItem>
              ))}
            </ScrollStagger>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

function MobileCell({
  header,
  value,
  highlighted = false,
  showAsterisk = false,
}: {
  header: string;
  value: string;
  highlighted?: boolean;
  showAsterisk?: boolean;
}) {
  const isCheck = value === "✓";
  const isMinus = value === "—" || value === "-";
  return (
    <div
      className={`flex flex-col items-center gap-1 px-2 py-2 text-center ${
        highlighted ? "bg-aurora-cyan/5" : ""
      }`}
    >
      <span
        className={`font-mono text-[9px] uppercase tracking-[0.16em] ${
          highlighted ? "text-aurora-cyan" : "text-muted-foreground/70"
        }`}
      >
        {header}
      </span>
      <span
        className={`text-xs leading-tight ${
          highlighted ? "font-medium text-foreground" : "text-foreground/80"
        }`}
      >
        {isCheck ? (
          <Check className={`size-4 ${highlighted ? "text-aurora-cyan" : "text-foreground/70"}`} aria-hidden />
        ) : isMinus ? (
          <Minus className="size-4 text-muted-foreground/60" aria-hidden />
        ) : value.startsWith("✓ ") && highlighted ? (
          <span className="inline-flex items-center gap-1">
            <Check className="size-3 shrink-0 text-aurora-cyan" aria-hidden />
            <span>{value.slice(2)}</span>
          </span>
        ) : (
          value
        )}
        {showAsterisk && <DelayAsterisk />}
      </span>
    </div>
  );
}

function ColumnHead({
  label,
  muted = false,
  highlighted = false,
}: {
  label: string;
  muted?: boolean;
  highlighted?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-center px-5 py-5 text-center font-mono text-[11px] uppercase tracking-[0.18em] ${
        highlighted
          ? "border-x border-aurora-cyan/40 bg-aurora-cyan/8 font-semibold text-foreground"
          : muted
            ? "text-muted-foreground"
            : "text-muted-foreground"
      }`}
    >
      {label}
    </div>
  );
}

function Cell({
  value,
  muted = false,
  highlighted = false,
  showAsterisk = false,
}: {
  value: string;
  muted?: boolean;
  highlighted?: boolean;
  showAsterisk?: boolean;
}) {
  const isCheck = value === "✓";
  const isMinus = value === "—" || value === "-";

  return (
    <div
      className={`flex items-center justify-center px-5 py-5 text-center text-sm ${
        highlighted
          ? "border-x border-aurora-cyan/30 bg-aurora-cyan/5 font-medium text-foreground"
          : muted
            ? "text-muted-foreground"
            : "text-foreground"
      }`}
    >
      {isCheck ? (
        <Check className={`size-4 ${highlighted ? "text-aurora-cyan" : "text-foreground/70"}`} aria-hidden />
      ) : isMinus ? (
        <Minus className="size-4 text-muted-foreground/60" aria-hidden />
      ) : (
        <span className="leading-tight">
          {value.startsWith("✓ ") && highlighted ? (
            <span className="inline-flex items-center gap-1.5">
              <Check className="size-4 shrink-0 text-aurora-cyan" aria-hidden />
              <span>{value.slice(2)}</span>
            </span>
          ) : (
            value
          )}
          {showAsterisk && <DelayAsterisk />}
        </span>
      )}
    </div>
  );
}
